package com.patronat.top.features.venues.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Popup
import com.patronat.top.core.domain.models.profiles.User
import com.patronat.top.dto.UserRole
import com.patronat.top.dto.VenueResponse
import com.patronat.top.dto.venues.VenueStatus
import com.patronat.top.ui.molecules.menu.ActionMenuItem
import com.patronat.top.ui.molecules.pagination.TablePagination
import com.patronat.top.ui.molecules.status.StatusBadge
import com.patronat.top.ui.molecules.status.StatusBadgeColors
import com.patronat.top.ui.organisms.table.AppDataTable
import com.patronat.top.ui.organisms.table.TableColumn
import com.patronat.top.ui.theme.AccentIndigo
import com.patronat.top.ui.theme.AccentMint
import com.patronat.top.ui.theme.AccentOrange
import com.patronat.top.ui.theme.AccentPink
import com.patronat.top.ui.theme.ActiveStatusStroke
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.DropdownBorder
import com.patronat.top.ui.theme.LabelLarge
import com.patronat.top.ui.theme.MineShaft400
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft700Strong
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary700
import com.patronat.top.ui.theme.White
import kotlinx.datetime.Instant
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import top.features.venues.generated.resources.Res
import top.features.venues.generated.resources.venue_action_block
import top.features.venues.generated.resources.venue_action_view_edit
import top.features.venues.generated.resources.venue_cd_actions_menu
import top.features.venues.generated.resources.venue_status_added_by_moderator
import top.features.venues.generated.resources.venue_status_application
import top.features.venues.generated.resources.venue_status_check_changes
import top.features.venues.generated.resources.venue_status_connected
import top.features.venues.generated.resources.venue_status_draft
import top.features.venues.generated.resources.venue_status_pending_review
import top.features.venues.generated.resources.venue_status_rejected
import top.features.venues.generated.resources.venue_status_closed
import top.features.venues.generated.resources.venue_table_col_actions
import top.features.venues.generated.resources.venue_table_col_date
import top.features.venues.generated.resources.venue_table_col_email
import top.features.venues.generated.resources.venue_table_col_moderator
import top.features.venues.generated.resources.venue_table_col_name
import top.features.venues.generated.resources.venue_table_col_phone
import top.features.venues.generated.resources.venue_table_col_status
import top.features.venues.generated.resources.venue_table_placeholder_dash
import top.features.venues.generated.resources.venue_owner_title

@Composable
fun VenuesTable(
    venues: List<VenueResponse>,
    venueModeratorsById: Map<String, User>,
    venueOwnersById: Map<String, User>,
    totalCount: Long,
    currentPage: Int,
    pageSize: Int,
    expandedVenueMenuId: String?,
    canBlockVenue: Boolean,
    onPageChange: (Int) -> Unit,
    onPageSizeChange: (Int) -> Unit,
    onActionClick: (String) -> Unit,
    onDismissMenu: () -> Unit,
    onEditVenue: (String) -> Unit,
    onBlockVenue: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val titleModerator = stringResource(Res.string.venue_table_col_moderator)
    val titleName = stringResource(Res.string.venue_table_col_name)
    val titleOwner = stringResource(Res.string.venue_owner_title)
    val titlePhone = stringResource(Res.string.venue_table_col_phone)
    val titleEmail = stringResource(Res.string.venue_table_col_email)
    val titleStatus = stringResource(Res.string.venue_table_col_status)
    val titleDate = stringResource(Res.string.venue_table_col_date)
    val titleActions = stringResource(Res.string.venue_table_col_actions)

    val actionEdit = stringResource(Res.string.venue_action_view_edit)
    val actionBlock = stringResource(Res.string.venue_action_block)
    val actionDesc = stringResource(Res.string.venue_cd_actions_menu)
    val placeholder = stringResource(Res.string.venue_table_placeholder_dash)

    val columns = listOf(
        TableColumn<VenueResponse>(title = titleModerator, widthWeight = 1.3f, alignment = Alignment.Start) { venue ->
            val moderator = venue.moderatorId
                ?.let { venueModeratorsById[it] }
                ?: venueOwnersById[venue.ownerId]?.takeIf { it.role.isModeratorOrSuperAdmin() }
            val moderatorName = moderator?.toDisplayName(placeholder) ?: placeholder
            Text(
                text = moderatorName,
                style = primaryCellTextStyle(),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        },
        TableColumn<VenueResponse>(title = titleName, widthWeight = 1.5f, alignment = Alignment.Start) { venue ->
            Text(
                text = venue.name,
                style = primaryCellTextStyle(),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        },
        TableColumn<VenueResponse>(title = titleOwner, widthWeight = 1.3f, alignment = Alignment.Start) { venue ->
            val owner = venueOwnersById[venue.ownerId]
                ?.takeUnless { it.role.isModeratorOrSuperAdmin() }
            val ownerName = owner?.toDisplayName(placeholder) ?: placeholder
            Text(
                text = ownerName,
                style = secondaryCellTextStyle(),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        },
        TableColumn<VenueResponse>(title = titlePhone, widthWeight = 1.2f, alignment = Alignment.Start) { venue ->
            val owner = venueOwnersById[venue.ownerId]
                ?.takeUnless { it.role.isModeratorOrSuperAdmin() }
            val phone = owner?.phone?.ifBlank { placeholder } ?: placeholder
            Text(
                text = phone,
                style = secondaryCellTextStyle(),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        },
        TableColumn<VenueResponse>(title = titleEmail, widthWeight = 1.4f, alignment = Alignment.Start) { venue ->
            val owner = venueOwnersById[venue.ownerId]
                ?.takeUnless { it.role.isModeratorOrSuperAdmin() }
            val email = owner?.email?.ifBlank { placeholder } ?: placeholder
            Text(
                text = email,
                style = secondaryCellTextStyle(),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        },
        TableColumn<VenueResponse>(title = titleStatus, widthWeight = 1.1f, alignment = Alignment.CenterHorizontally) { venue ->
            val statusText = stringResource(venue.status.toStatusLabelRes())
            StatusBadge(
                text = statusText,
                colors = venue.status.toBadgeColors(),
                showDot = false,
                minHeight = 26.dp,
                cornerRadius = 10.dp,
                borderWidth = 0.73.dp,
                horizontalPadding = 10.dp,
                verticalPadding = 4.dp
            )
        },
        TableColumn<VenueResponse>(title = titleDate, widthWeight = 0.9f, alignment = Alignment.CenterHorizontally) { venue ->
            Text(
                text = venue.createdAt.toDisplayString(placeholder),
                style = secondaryCellTextStyle(),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        },
        TableColumn<VenueResponse>(title = titleActions, widthWeight = 0.6f, alignment = Alignment.CenterHorizontally) { venue ->
            Box(modifier = Modifier.wrapContentWidth()) {
                IconButton(onClick = { onActionClick(venue.id) }) {
                    Icon(
                        painter = AppIcons.Action3Dot,
                        contentDescription = actionDesc,
                        tint = MineShaft500
                    )
                }

                if (expandedVenueMenuId == venue.id) {
                    Popup(
                        onDismissRequest = onDismissMenu,
                        alignment = Alignment.BottomEnd
                    ) {
                        Column(
                            modifier = Modifier
                                .width(224.dp)
                                .background(White, RoundedCornerShape(12.dp))
                                .border(0.75.dp, DropdownBorder, RoundedCornerShape(12.dp))
                                .padding(5.dp)
                        ) {
                            ActionMenuItem(
                                text = actionEdit,
                                icon = AppIcons.ViewVenue,
                                onClick = { onEditVenue(venue.id) }
                            )
                            if (canBlockVenue && venue.status != VenueStatus.REJECTED) {
                                ActionMenuItem(
                                    text = actionBlock,
                                    icon = AppIcons.Block,
                                    onClick = { onBlockVenue(venue.id) },
                                    textColor = AccentPink
                                )
                            }
                        }
                    }
                }
            }
        }
    )

    Column(modifier = modifier.fillMaxSize()) {
        AppDataTable(
            columns = columns,
            data = venues,
            modifier = Modifier.weight(1f)
        )

        TablePagination(
            currentPage = currentPage,
            totalItems = totalCount.toInt(),
            pageSize = pageSize,
            onPageChange = onPageChange,
            onPageSizeChange = onPageSizeChange
        )
    }
}

@Composable
private fun primaryCellTextStyle(): TextStyle {
    return LabelLarge.copy(
        fontWeight = FontWeight.SemiBold,
        fontSize = 12.sp,
        lineHeight = 16.sp,
        color = MineShaft950
    )
}

@Composable
private fun secondaryCellTextStyle(): TextStyle {
    return LabelLarge.copy(
        fontWeight = FontWeight.SemiBold,
        fontSize = 12.sp,
        lineHeight = 16.sp,
        color = MineShaft500
    )
}

private fun User.toDisplayName(placeholder: String): String {
    return listOf(firstName, lastName)
        .filter { it.isNotBlank() }
        .joinToString(" ")
        .ifBlank { placeholder }
}

private fun VenueStatus.toStatusLabelRes(): StringResource {
    return when (this) {
        VenueStatus.ON_MODERATION -> Res.string.venue_status_pending_review
        VenueStatus.APPLICATION -> Res.string.venue_status_application
        VenueStatus.CONNECTED -> Res.string.venue_status_connected
        VenueStatus.REJECTED -> Res.string.venue_status_rejected
        VenueStatus.ADDED_BY_MODERATOR -> Res.string.venue_status_added_by_moderator
        VenueStatus.CHECK_CHANGES -> Res.string.venue_status_check_changes
        VenueStatus.CLOSED -> Res.string.venue_status_closed
        VenueStatus.DRAFT -> Res.string.venue_status_draft
    }
}

private fun VenueStatus.toBadgeColors(): StatusBadgeColors {
    return when (this) {
        VenueStatus.ON_MODERATION -> StatusBadgeColors(
            bgColor = White,
            contentColor = Primary700,
            borderColor = Primary700
        )
        VenueStatus.APPLICATION -> StatusBadgeColors(
            bgColor = White,
            contentColor = MineShaft700Strong,
            borderColor = MineShaft700Strong
        )
        VenueStatus.CONNECTED -> StatusBadgeColors(
            bgColor = White,
            contentColor = ActiveStatusStroke,
            borderColor = ActiveStatusStroke
        )
        VenueStatus.REJECTED -> StatusBadgeColors(
            bgColor = White,
            contentColor = AccentPink,
            borderColor = AccentPink
        )
        VenueStatus.ADDED_BY_MODERATOR -> StatusBadgeColors(
            bgColor = White,
            contentColor = AccentMint,
            borderColor = AccentMint
        )
        VenueStatus.CHECK_CHANGES -> StatusBadgeColors(
            bgColor = White,
            contentColor = AccentIndigo,
            borderColor = AccentIndigo
        )
        VenueStatus.CLOSED -> StatusBadgeColors(
            bgColor = White,
            contentColor = MineShaft400,
            borderColor = MineShaft400
        )
        VenueStatus.DRAFT -> StatusBadgeColors(
            bgColor = White,
            contentColor = AccentOrange,
            borderColor = AccentOrange
        )
    }
}

private fun Instant?.toDisplayString(placeholder: String): String {
    if (this == null) return placeholder
    return try {
        this.toString().take(10).split("-").reversed().joinToString(".")
    } catch (e: Exception) {
        this.toString()
    }
}

private fun UserRole.isModeratorOrSuperAdmin(): Boolean {
    return this == UserRole.MODERATOR || this == UserRole.SUPER_ADMIN
}
